import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Avatar, Button, Box, CircularProgress, Typography, Card } from "@mui/joy";

import { supabase } from "../supabaseClient";
import { fetchProfilePicture, getInitials } from "../utils";
import { StatusBadge } from "../utils/classes";

import { useModal } from "../context/ModalContext";
import { useToast } from "../context/ToastContext";

import { ClassStatus, Teacher } from "../types";
import { Class, calculateClassStatus } from "../types/Class";

import Page, { Breadcrumb } from "../components/Page";
import AddEditClassModal from "../components/modals/AddEditClassModal";
import ClassStudentsTable from "../components/tables/ClassStudentsTable";
import ClassAttendanceTable from "../components/tables/ClassAttendanceTable";
import { formatScheduleDisplay } from "../utils/schedule-formatting";

const ClassDetailView: React.FC = () => {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { openModal } = useModal();
  const { showToast } = useToast();
  const [classData, setClassData] = React.useState<Class | null>(null);
  const [breadcrumbs, setBreadcrumbs] = React.useState<Breadcrumb[]>([
    { label: "Home", href: "/" },
    { label: "Classes", href: "/classes" },
    { label: "Viewing Class" },
  ]);
  const [enrolledStudentCount, setEnrolledStudentCount] = useState(0);

  // Fetch a single class with relationships and profile pictures
  const fetchClass = async (classId: string | null | undefined) => {
    if (!classId) {
      return null;
    }

    try {
      const { data, error } = await supabase
        .from("classes_view")
        .select(`
          *,
          primary_teacher:primary_teacher_id (*),
          secondary_teacher:secondary_teacher_id (*)
        `)
        .eq("id", classId)
        .single();

      if (error || !data) {
        throw error;
      }

      const primaryTeacher = data.primary_teacher || null;
      const secondaryTeacher = data.secondary_teacher || null;

      // Fetch profile pictures for both primary and secondary teachers
      const primaryTeacherWithProfile = primaryTeacher ? {
        ...primaryTeacher,
        profile_picture: await fetchProfilePicture("teachers", primaryTeacher.profile_picture_id),
        first_name: primaryTeacher.first_name || "",
        last_name: primaryTeacher.last_name || ""
      } as Teacher : null;
      
      const secondaryTeacherWithProfile = secondaryTeacher ? {
        ...secondaryTeacher,
        profile_picture: await fetchProfilePicture("teachers", secondaryTeacher.profile_picture_id),
        first_name: secondaryTeacher.first_name || "",
        last_name: secondaryTeacher.last_name || ""
      } as Teacher : null;

      return {
        ...data,
        primary_teacher: primaryTeacherWithProfile,
        secondary_teacher: secondaryTeacherWithProfile,
      } as Class;
    } catch (error) {
      console.error("Error fetching class:", error);
      return null;
    }
  };

  useEffect(() => {
    fetchClass(classId).then((classData) => {
      setClassData(classData);
    });
  }, [classId]);

  useEffect(() => {
    if (classData) {
      setBreadcrumbs([
        { label: "Home", href: "/" },
        { label: "Classes", href: "/classes" },
        { label: `${classData.start_month}/${classData.start_year} - ${classData.end_month}/${classData.end_year}` },
      ]);
    }
  }, [classData]);

  // Subscribe to real-time changes for this specific class
  useEffect(() => {
    if (!classId) return;

    const subscription = supabase
      .channel("public:classes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "classes", filter: `id=eq.${classId}` },
        (payload) => {
          fetchClass(classId).then((updatedClass) => {
            setClassData(updatedClass);
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [classId]);

  if (!classData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const handleEditClass = () => {
    const transformedClassData = {
      ...classData,
      schedule: {
        monday: { 
          start: classData.schedule.monday?.start || null,
          end: classData.schedule.monday?.end || null,
          hasSchool: classData.schedule.monday?.hasSchool ?? true
        },
        tuesday: { 
          start: classData.schedule.tuesday?.start || null,
          end: classData.schedule.tuesday?.end || null,
          hasSchool: classData.schedule.tuesday?.hasSchool ?? true
        },
        wednesday: { 
          start: classData.schedule.wednesday?.start || null,
          end: classData.schedule.wednesday?.end || null,
          hasSchool: classData.schedule.wednesday?.hasSchool ?? true
        },
        thursday: { 
          start: classData.schedule.thursday?.start || null,
          end: classData.schedule.thursday?.end || null,
          hasSchool: classData.schedule.thursday?.hasSchool ?? true
        },
        friday: { 
          start: classData.schedule.friday?.start || null,
          end: classData.schedule.friday?.end || null,
          hasSchool: classData.schedule.friday?.hasSchool ?? true
        },
        saturday: { 
          start: classData.schedule.saturday?.start || null,
          end: classData.schedule.saturday?.end || null,
          hasSchool: classData.schedule.saturday?.hasSchool ?? false
        },
        sunday: { 
          start: classData.schedule.sunday?.start || null,
          end: classData.schedule.sunday?.end || null,
          hasSchool: classData.schedule.sunday?.hasSchool ?? false
        }
      }
    };
    openModal(<AddEditClassModal classData={transformedClassData} />);
  };

  const handleDeleteClass = async () => {
    if (!classData) return;

    try {
      const { error } = await supabase
        .from("classes")
        .delete()
        .eq("id", classData.id);

      if (error) {
        showToast("Error deleting class.", "danger", "solid");
      } else {
        showToast("Class deleted successfully.", "success", "solid");
        navigate("/classes");
      }
    } catch (error) {
      console.error("Error deleting class:", error);
      showToast("Error deleting class.", "danger", "solid");
    }
  };

  return (
    <Page breadcrumbs={breadcrumbs}>
      <Card
        variant="soft"
        sx={{
          display: "flex",
          alignItems: "flex-start",
          position: "relative",
          p: 3,
          mb: 4,
          width: "100%",
          backgroundColor: "background.level2",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, flex: 1, width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <Typography level="h2" component="h1">
              Class Details
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                onClick={handleEditClass}
                variant="solid"
                color="primary"
              >
                Edit
              </Button>
              {calculateClassStatus(classData) === ClassStatus.Future && (
                <Button
                  onClick={handleDeleteClass}
                  variant="solid"
                  color="danger"
                >
                  Delete
                </Button>
              )}
            </Box>
          </Box>

          {classData.primary_teacher && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography component="span" fontWeight="bold">
                Primary Teacher:
              </Typography>
              <Avatar
                src={classData.primary_teacher.profile_picture || undefined}
                sx={{ width: 32, height: 32 }}
              >
                {!classData.primary_teacher.profile_picture && classData.primary_teacher.first_name && classData.primary_teacher.last_name
                  ? getInitials(classData.primary_teacher)
                  : null}
              </Avatar>
              <Typography>
                {classData.primary_teacher.first_name} {classData.primary_teacher.last_name}
              </Typography>
            </Box>
          )}
          {classData.secondary_teacher && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography component="span" fontWeight="bold">
                Secondary Teacher:
              </Typography>
              <Avatar
                src={classData.secondary_teacher.profile_picture || undefined}
                sx={{ width: 32, height: 32 }}
              >
                {!classData.secondary_teacher.profile_picture && classData.secondary_teacher.first_name && classData.secondary_teacher.last_name
                  ? getInitials(classData.secondary_teacher)
                  : null}
              </Avatar>
              <Typography>
                {classData.secondary_teacher.first_name} {classData.secondary_teacher.last_name}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography component="span" fontWeight="bold">
              Status:
            </Typography>
            <StatusBadge status={calculateClassStatus(classData)} />
          </Box>
          <Typography>
            <Typography component="span" fontWeight="bold">
              Term:
            </Typography>{" "}
            {classData.start_month}/{classData.start_year} - {classData.end_month}/{classData.end_year}
          </Typography>
          <Typography>
            <Typography component="span" fontWeight="bold">
              Schedule:
            </Typography>{" "}
            {formatScheduleDisplay(classData.schedule)}
          </Typography>
        </Box>
      </Card>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          mb: 1,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "start", sm: "center" },
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography level="h3" component="h2">
            Enrolled Students ({enrolledStudentCount})
          </Typography>
        </Box>
        <ClassStudentsTable 
          classId={classId} 
          onEnrolledCountChange={setEnrolledStudentCount}
        />
      </Box>

      <Box
        sx={{
          width: "100%",
          display: "flex",
          mb: 1,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "start", sm: "center" },
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Typography level="h3" component="h2">
          Attendance Records
        </Typography>
        <ClassAttendanceTable classId={classId} />
      </Box>
    </Page>
  );
};

export default ClassDetailView; 